import { monthsToYears } from 'date-fns';
import _ from 'lodash';
import {
  LicenseAuditHistory,
  LicenseAuditHistoryDifference,
} from '../../stores/types/licenses.interface';
import {
  convertToDate,
  formatDate,
  getDifferenceInYearsAndDaysString,
} from '../../utils/Date.Util';
import { TimelineData } from '../_ui/time-line/types/Timeline.interface';

export const LICENSE_STATUS = {
  ACTIVE: 'active',
  ASSIGNED: 'assigned',
  UNASSIGNED: 'unassigned',
  EXPIRED: 'expired',
};

export const LICENSE_AUDIT_EVENT_TITLE = {
  LICENSE_CREATED: 'Created',
  LICENSE_ASSIGNED: 'Assigned to charger {}',
  LICENSE_UNASSIGNED: 'Unassigned',
  LICENSE_EXPIRED: 'Expired',
  LICENSE_ACTIVATED: 'Activated',
  LICENSE_CHURNED: 'Churned',
  LICENSE_DELETED: 'Deleted',
  LICENSE_RENEWED: 'Renewed {}',
  LICENSE_EDITED: 'Edited',
  LICENSE_MOVED: 'Moved',
} as const;

export const FOUR_G_STATUS = {
  YES: 'yes',
  NO: 'no',
};

export const durationTypeValues = {
  ONE_YEAR: 12,
  TWO_YEARS: 24,
  THREE_YEARS: 36,
  FOUR_YEARS: 48,
  FIVE_YEARS: 60,
};

export const DURATION_TYPES = {
  ONE_YEAR: 'one_year',
  TWO_YEARS: 'two_years',
  THREE_YEARS: 'three_years',
  FOUR_YEARS: 'four_years',
  FIVE_YEARS: 'five_years',
};

export const PORT_COUNT = {
  ONE: 1,
  TWO: 2,
};

export const licenseTypeValues = {
  LEVEL_2_SINGLE_PORT: 'STD L2 - single',
  LEVEL_2_DUAL_PORT: 'STD L2 - dual',
  DC_FAST_SINGLE_PORT: 'STD DCFC - single',
  DC_FAST_DUAL_PORT: 'STD DCFC - dual',
  ESSENTIALS_SINGLE_PORT: 'Essentials - single',
  ESSENTIALS_DUAL_PORT: 'Essentials - dual',
  ELA_L2_SINGLE_PORT: 'ELA L2 - single',
  ELA_L2_DUAL_PORT: 'ELA L2 - dual',
  ELA_DC_SINGLE_PORT: 'ELA DC - single',
  ELA_DC_DUAL_PORT: 'ELA DC - dual',
  HOME_HOME: 'Home',
};

export const LICENSE_TYPES = {
  LEVEL_2: 'level_2',
  DC_FAST: 'dc_fast',
  ESSENTIALS: 'essentials',
  ELA_L2: 'ela_l2',
  ELA_DC: 'ela_dc',
  HOME: 'home',
};

export const LICENSE_PORT_TYPES = {
  SINGLE_PORT: 'single_port',
  DUAL_PORT: 'dual_port',
};

export const FILTER_TYPES = {
  STATUS: 'status',
  DURATION: 'duration',
  TYPE: 'type',
  INCLUDES_4G: 'includes4G',
  CHARGER_ID: 'chargerId',
  SEARCH_VALUE: 'searchValue',
};

export const LICENSE_LEVEL_TYPES = {
  LEVEL_2: 'level 2',
  DC_FAST: 'level 3',
  ELA_L2: 'level 2',
  ELA_DC: 'level 3',
} as const;

export const INTERNAL_EDITABLE_FIELDS_TO_HUMAN_READABLE_FIELDS = {
  type: 'License Type',
  ports: 'Ports',
  duration: 'Duration',
  includes4G: 'Includes ChargeLab 4G?',
  orderNote: 'Order Note',
};

export enum LicenseAuditEventType {
  CREATED = 'LICENSE_CREATED',
  ASSIGNED = 'LICENSE_ASSIGNED',
  UNASSIGNED = 'LICENSE_UNASSIGNED',
  EXPIRED = 'LICENSE_EXPIRED',
  ACTIVATED = 'LICENSE_ACTIVATED',
  CHURNED = 'LICENSE_CHURNED',
  DELETED = 'LICENSE_DELETED',
  RENEWED = 'LICENSE_RENEWED',
  EDITED = 'LICENSE_EDITED',
  MOVED = 'LICENSE_MOVED',
}

type LicenseLevelType = keyof typeof LICENSE_LEVEL_TYPES;

type InternalField =
  keyof typeof INTERNAL_EDITABLE_FIELDS_TO_HUMAN_READABLE_FIELDS;

export const getRemainingDays = (
  startDate: string | Date,
  endDate: string | Date,
) => {
  const { years, remainingDays: days } = getDifferenceInYearsAndDaysString(
    startDate,
    endDate,
  );

  return { years, days };
};

/**
 * This function takes a value and maps it to its corresponding license type. If the input
 * value doesn't match any known license type, it returns the original value.
 *
 * The conversion process:
 *  1. Finds the key in licenseTypeValues that matches the input value
 *  2. Converts the key to lowercase
 *  3. Finds the appropriate LICENSE_TYPES entry that matches the beginning of the key
 *  4. Returns the matching LICENSE_TYPES key in lowercase, or the original value if no match
 *
 * @param {string} value - value to convert (e.g., "STD L2 - single")
 * @returns {string} The corresponding license type (e.g., "level_2"), or the original value if no match found
 *
 * @example
 * // Returns "level_2"
 * getLicenseType("STD L2 - single")
 *
 * @example
 * // Returns "ela_dc"
 * getLicenseType("ELA DC - dual")
 *
 * @example
 * // Returns "ela_dc"
 * getLicenseType("ela_dc")
 *
 * @example
 * // Returns "unknown_type" (original value)
 * getLicenseType("unknown_type")
 */
export const getLicenseType = (value: string) => {
  const typeKey = Object.entries(licenseTypeValues)
    .find(([, val]) => val === value)?.[0]
    ?.toLowerCase();
  return (
    Object.entries(LICENSE_TYPES)
      .find(([, val]) => typeKey?.startsWith(val))?.[0]
      ?.toLowerCase() || value
  );
};

export const getInternalLicenseType = (value: string, ports?: number) => {
  const typeKey = Object.entries(LICENSE_TYPES)
    .find(([, val]) => val === value)?.[0]
    ?.toLowerCase();

  // Return the original value if typeKey is undefined
  if (!typeKey) {
    return value;
  }
  // Find the entry with matching typeKey
  const entries = Object.entries(licenseTypeValues).filter(
    ([key]) => key && key.toLowerCase().includes(typeKey),
  );

  // Return base type without port suffix for home/demo or when ports not specified
  if (!ports || [LICENSE_TYPES.HOME].includes(value)) {
    return entries[0]?.[1].split(' -')[0] || value;
  }

  const suffix =
    ports === PORT_COUNT.ONE
      ? LICENSE_PORT_TYPES.SINGLE_PORT
      : LICENSE_PORT_TYPES.DUAL_PORT;

  const typeKeyWithSuffix = typeKey.concat('_', suffix);

  // Find the entry with matching ports (single/dual)
  return (
    entries.find(
      ([key]) => key.toLowerCase() === typeKeyWithSuffix.toLowerCase(),
    )?.[1] || value
  );
};

export const daysDifferenceText = (
  isExpired: boolean,
  expDate: string | Date,
  t: any,
) => {
  const daysObj = isExpired
    ? getRemainingDays(expDate, new Date())
    : getRemainingDays(new Date(), expDate);

  const { years, days } = daysObj;

  const yearText = `${years}  ${t(years > 1 ? 'years' : 'year')}`;
  const dayText = `${days} ${t(days > 1 ? 'days' : 'day')}`;

  return years > 0 ? `${yearText} ${dayText}` : dayText;
};

export const isLicenseTypeHome = (licenseType: string) => {
  return licenseType === LICENSE_TYPES.HOME;
};

export const isNoDurationType = (licenseType: string) => {
  const allowedPrefixes = ['ela', 'essential', 'home'];
  return allowedPrefixes.some((prefix) =>
    licenseType?.toLowerCase().startsWith(prefix),
  );
};

export const isLicenseTypeHomeEssentialOrEla = (licenseType: string) => {
  return [
    LICENSE_TYPES.HOME,
    LICENSE_TYPES.ESSENTIALS,
    LICENSE_TYPES.ELA_L2,
    LICENSE_TYPES.ELA_DC,
  ].includes(licenseType);
};

export const getLicenseLevelType = (licenseType: string): string => {
  const readableType = getLicenseType(licenseType);
  const key = readableType.toUpperCase() as LicenseLevelType;
  return LICENSE_LEVEL_TYPES[key] || readableType;
};

export const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const getObjectDiff = (
  obj1: any,
  obj2: any,
  getChargerNameById: (id: string) => string,
): string => {
  const changes: Record<string, any> = {};

  Object.keys(obj2).forEach((key) => {
    if (!_.isEqual(obj1[key], obj2[key])) {
      changes[key] = obj2[key];
    }
  });

  if ('status' in changes) {
    if (changes.status === LICENSE_STATUS.ASSIGNED.toUpperCase()) {
      return `Assigned to charger ${getChargerNameById(
        changes.assignedCharger,
      )}`;
    }
    return capitalizeFirstLetter(changes.status);
  }

  return 'No status change.';
};

export const getAuditEventLabel = (eventKey: string): string => {
  const key = eventKey as keyof typeof LICENSE_AUDIT_EVENT_TITLE; // Ensure TypeScript treats it as a valid key

  return LICENSE_AUDIT_EVENT_TITLE[key];
};

/**
 * Given two audit entries, parse their details and return
 * an array of fields whose values differ.
 */
export function getLicenseAuditHistoryDifference(
  prevAuditDetails: string,
  currAuditDetails: string,
  fieldsToCheckForDifference: string[],
): LicenseAuditHistoryDifference[] {
  const prev = JSON.parse(prevAuditDetails);
  const curr = JSON.parse(currAuditDetails);

  const auditHistoryDifference: LicenseAuditHistoryDifference[] = [];

  fieldsToCheckForDifference.forEach((key) => {
    // Only add to difference if the field exists in at least one of the objects
    // and the values are different
    if ((key in prev || key in curr) && prev[key] !== curr[key]) {
      auditHistoryDifference.push({
        field: key,
        oldValue: prev[key],
        newValue: curr[key],
      });
    }
  });

  return auditHistoryDifference;
}

export const transformInternalFieldsToHumanReadableFields = (
  internalField: string,
) => {
  return INTERNAL_EDITABLE_FIELDS_TO_HUMAN_READABLE_FIELDS[
    internalField as InternalField
  ];
};

export const generateLicenseAuditHistory = (
  auditLogs: LicenseAuditHistory[],
  getChargerNameById: (id: string) => string,
  companies: any,
): TimelineData[] => {
  return auditLogs.map((log, index) => {
    const { auditEvent, auditEventUpdatedBy, auditDetails } = log;
    const auditEventTime = new Date(`${log.auditEventTime}Z`);
    let licenseHistoryTitle = getAuditEventLabel(auditEvent);
    let additionalInfo = '';
    if (
      auditEvent === LicenseAuditEventType.ASSIGNED &&
      auditDetails.includes('assignedCharger')
    ) {
      licenseHistoryTitle = licenseHistoryTitle.replace(
        '{}',
        getChargerNameById(JSON.parse(auditDetails).assignedCharger) ||
          `at ${
            companies?.find(
              (c: { id: any }) => c.id === JSON.parse(auditDetails).companyId,
            )?.name || ''
          }`,
      );
    }
    if (auditEvent === 'LICENSE_MOVED') {
      // Find the previous audit log for the same license
      const prevLog = auditLogs
        .slice(index + 1)
        .find((prevItem) => prevItem.licenseId === log.licenseId);

      if (prevLog) {
        licenseHistoryTitle = `Moved from ${
          companies?.find(
            (c: { id: any }) =>
              c.id === JSON.parse(prevLog.auditDetails).companyId,
          )?.name || ''
        } to ${
          companies?.find(
            (c: { id: any }) => c.id === JSON.parse(auditDetails).companyId,
          )?.name || ''
        }`;
      }
    }
    if (auditEvent === LicenseAuditEventType.MOVED) {
      // Find the previous audit log for the same license
      const prevLog = auditLogs
        .slice(index + 1)
        .find((prevItem) => prevItem.licenseId === log.licenseId);

      if (prevLog) {
        licenseHistoryTitle = `Moved from ${
          companies?.find(
            (c: { id: any }) =>
              c.id === JSON.parse(prevLog.auditDetails).companyId,
          )?.name || ''
        } to ${
          companies?.find(
            (c: { id: any }) => c.id === JSON.parse(auditDetails).companyId,
          )?.name || ''
        }`;
      }
    }
    if (auditEvent === LicenseAuditEventType.EDITED) {
      // Find the previous audit log for the same license
      const prevLog = auditLogs
        .slice(index + 1)
        .find((prevItem) => prevItem.licenseId === log.licenseId);

      if (prevLog) {
        additionalInfo = getLicenseAuditHistoryDifference(
          prevLog.auditDetails,
          auditDetails,
          Object.keys(INTERNAL_EDITABLE_FIELDS_TO_HUMAN_READABLE_FIELDS),
        )
          .filter((diff) => diff.newValue !== undefined)
          .map((diff) => {
            // Format duration as years
            if (diff.field === 'duration') {
              return `"${transformInternalFieldsToHumanReadableFields(
                diff.field,
              )}" changed to "${monthsToYears(Number(diff.newValue))} years"`;
            }
            return `"${transformInternalFieldsToHumanReadableFields(
              diff.field,
            )}" changed to "${diff.newValue}"`;
          })
          .join(', ');
      }
    }
    if (auditEvent === LicenseAuditEventType.RENEWED) {
      // Find the previous audit log for the same license
      const prevLog = auditLogs
        .slice(index + 1)
        .find((prevItem) => prevItem.licenseId === log.licenseId);

      if (prevLog) {
        const auditDifference = getLicenseAuditHistoryDifference(
          prevLog.auditDetails,
          auditDetails,
          ['duration', 'expirationDate'],
        ).filter(
          (diff: LicenseAuditHistoryDifference) => diff.newValue !== undefined,
        );

        auditDifference.forEach((diff: LicenseAuditHistoryDifference) => {
          if (diff.field === 'duration') {
            licenseHistoryTitle = licenseHistoryTitle.replace(
              '{}',
              `${
                monthsToYears(Number(diff.newValue)) === 1
                  ? '(1 year)'
                  : `(${monthsToYears(Number(diff.newValue))} years)`
              }`,
            );
          }
          if (diff.field === 'expirationDate') {
            additionalInfo = `Now expires: ${formatDate(
              convertToDate(diff.newValue),
              'MMM d, yyyy',
            )}`;
          }
        });
      }
    }
    return {
      title: licenseHistoryTitle,
      status: auditEvent,
      updatedBy: auditEventUpdatedBy,
      date: auditEventTime,
      additionalInfo: additionalInfo,
    };
  });
};
